import { createQueryKeys } from "@lukemorales/query-key-factory";
import { useInfiniteQuery } from "@tanstack/vue-query";
import type {
  ISbCustomFetch,
  ISbResult,
  ISbStoriesParams,
} from "storyblok-js-client";
import type Storyblok from "storyblok-js-client/dist/types";

import type { ISbArticleStories } from "~/types/Storyblok";

const getStoryblokArticleApi = <R = ISbResult>({
  params,
  sbApi,
  slug,
  options,
}: {
  sbApi: Storyblok;
  slug: string;
  params?: ISbStoriesParams;
  options?: ISbCustomFetch;
}) =>
  sbApi.get(
    "cdn/stories",
    {
      ...params,
      starts_with: slug,
      content_type: "article",
      sort_by: "first_published_at:desc",
    },
    options,
  ) as Promise<R>;

/**
 * @deprecated
 * This will soon be replaced by the generic storyblok queries from `queries/storyblok/index.ts`.
 */
export const articleQueryKeys = createQueryKeys("articles", {
  list: (
    slug: MaybeRef<string>,
    params?: MaybeRef<ISbStoriesParams>,
    options?: MaybeRef<ISbCustomFetch>,
  ) => ({
    queryKey: [params, options],
    queryFn: async () => {
      const sbApi = useStoryblokApi();

      const response = await getStoryblokArticleApi<ISbArticleStories>({
        sbApi,
        slug: unref(slug),
        params: { ...unref(params) },
        options: unref(options),
      });

      return response.data;
    },
  }),
});

export const infiniteArticlesListQuery = () => {
  const sbApi = useStoryblokApi();
  const { slug, params } = useStoryblokRequestConfig({
    folderPath: "news",
    shouldAppendRouteSlugs: false,
  });

  return useInfiniteQuery({
    queryKey: ["articles", "infiniteList"],
    queryFn: ({ pageParam }) =>
      getStoryblokArticleApi<ISbArticleStories>({
        sbApi,
        slug: slug.value,
        params: {
          ...params.value,
          page: pageParam,
          per_page: 10,
        },
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages, lastPageParam) => {
      const totalFetchedStories = allPages
        .flatMap((paginatedArticles) => paginatedArticles.data)
        .flatMap((t) => t.stories);

      const nextPage = lastPageParam + 1;

      if (lastPage.total <= totalFetchedStories.length) return;

      return nextPage;
    },
    select: (data) => {
      if (!data.pages || data.pages.length === 0) return;

      const flattenedArticles = data.pages
        .flatMap((paginatedArticles) => paginatedArticles.data)
        .flatMap((t) => t.stories);

      return {
        total: data.pages?.[0]?.total ?? 0,
        data: flattenedArticles,
      };
    },
  });
};
